import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  SearchIcon, 
  FilterIcon, 
  CalendarIcon,
  UserIcon,
  FileTextIcon,
  TrendingUpIcon,
  ClockIcon,
  CheckCircleIcon,
  AlertCircleIcon,
  PauseIcon,
  X
} from 'lucide-react';
import api from '../../lib/api';
import toast from 'react-hot-toast';

interface CarePlan {
  id: string;
  patientId: string;
  visitId?: string;
  title: string;
  description?: string;
  diagnosis?: string;
  goals: any;
  interventions: any;
  startDate: string;
  endDate?: string;
  status: 'ACTIVE' | 'COMPLETED' | 'ON_HOLD';
  createdBy: string;
  assignedTo?: string;
  patient: {
    user: {
      firstName: string;
      lastName: string;
      email: string;
    };
  };
  visit?: {
    doctor: {
      user: {
        firstName: string;
        lastName: string;
      };
    };
  };
  createdAt: string;
  updatedAt: string;
}

interface Patient {
  id: string;
  user: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface Visit {
  id: string;
  visitType: string;
  visitDate: string;
  diagnosis: string;
  doctor: {
    user: {
      firstName: string;
      lastName: string;
    };
  };
}

const CarePlansPage: React.FC = () => {
  const [carePlans, setCarePlans] = useState<CarePlan[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [selectedPatientId, setSelectedPatientId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedCarePlan, setSelectedCarePlan] = useState<CarePlan | null>(null);
  const [analytics, setAnalytics] = useState<any>(null);

  useEffect(() => {
    fetchCarePlans();
    fetchAnalytics();
    fetchPatients();
  }, []);

  const fetchCarePlans = async () => {
    try {
      setLoading(true);
      const response = await api.get('/care-plans');
      setCarePlans(response.data.carePlans || []);
    } catch (error) {
      console.error('Error fetching care plans:', error);
      toast.error('Failed to fetch care plans');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/care-plans/analytics/overview');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await api.get('/patients');
      setPatients(response.data.data?.patients || []);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const fetchVisitsForPatient = async (patientId: string) => {
    try {
      const response = await api.get(`/visits?patientId=${patientId}`);
      setVisits(response.data.visits || []);
    } catch (error) {
      console.error('Error fetching visits for patient:', error);
      setVisits([]);
    }
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setSelectedPatientId('');
    setVisits([]);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const filteredCarePlans = carePlans.filter(plan => {
    const matchesSearch = 
      (plan.title?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (plan.diagnosis?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (plan.patient?.user?.firstName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (plan.patient?.user?.lastName?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    
    const matchesStatus = !statusFilter || plan.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-800';
      case 'COMPLETED': return 'bg-blue-100 text-blue-800';
      case 'ON_HOLD': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH': return 'bg-red-100 text-red-800';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800';
      case 'LOW': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ACTIVE': return <ClockIcon className="h-4 w-4" />;
      case 'COMPLETED': return <CheckCircleIcon className="h-4 w-4" />;
      case 'ON_HOLD': return <PauseIcon className="h-4 w-4" />;
      default: return <AlertCircleIcon className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Care Plans</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage patient care plans and treatment protocols
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          New Care Plan
        </button>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileTextIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Care Plans</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.total}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <ClockIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Active</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.active}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CheckCircleIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Completed</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.completed}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUpIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Completion Rate</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analytics.completionRate ? `${analytics.completionRate.toFixed(1)}%` : '0%'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search care plans..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="COMPLETED">Completed</option>
            <option value="ON_HOLD">On Hold</option>
          </select>
          
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Priorities</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>
        </div>
      </div>

      {/* Care Plans Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Start Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  End Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCarePlans.map((plan) => (
                <tr key={plan.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <UserIcon className="h-5 w-5 text-primary-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {plan.patient.user.firstName} {plan.patient.user.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {plan.patient.user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{plan.title}</div>
                    <div className="text-sm text-gray-500">{plan.diagnosis}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(plan.status)}`}>
                      {getStatusIcon(plan.status)}
                      <span className="ml-1">{plan.status}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(plan.startDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {plan.endDate ? formatDate(plan.endDate) : 'Ongoing'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button 
                      onClick={() => {
                        setSelectedCarePlan(plan);
                        setShowEditModal(true);
                      }}
                      className="text-primary-600 hover:text-primary-900 mr-3"
                    >
                      View
                    </button>
                    <button 
                      onClick={() => {
                        setSelectedCarePlan(plan);
                        setShowEditModal(true);
                        setIsEditMode(true);
                      }}
                      className="text-primary-600 hover:text-primary-900 mr-3"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to delete this care plan?')) {
                          try {
                            await api.delete(`/care-plans/${plan.id}`);
                            toast.success('Care plan deleted successfully');
                            fetchCarePlans();
                          } catch (error) {
                            console.error('Error deleting care plan:', error);
                            toast.error('Failed to delete care plan');
                          }
                        }
                      }}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredCarePlans.length === 0 && (
          <div className="text-center py-12">
            <FileTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No care plans found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new care plan.
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                New Care Plan
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Care Plan Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Create New Care Plan</h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const carePlanData = {
                  patientId: formData.get('patientId') as string,
                  visitId: formData.get('visitId') as string || undefined,
                  title: formData.get('title') as string,
                  diagnosis: formData.get('diagnosis') as string || undefined,
                  description: formData.get('description') as string || undefined,
                  goals: formData.get('goals') as string,
                  interventions: formData.get('interventions') as string,
                  startDate: formData.get('startDate') as string,
                  endDate: formData.get('endDate') as string || undefined,
                  status: 'ACTIVE' as const
                };

                try {
                  await api.post('/care-plans', carePlanData);
                  toast.success('Care plan created successfully');
                  handleCloseModal();
                  fetchCarePlans();
                } catch (error) {
                  console.error('Error creating care plan:', error);
                  toast.error('Failed to create care plan');
                }
              }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Patient Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Patient *
                    </label>
                    <select
                      name="patientId"
                      required
                      value={selectedPatientId}
                      onChange={(e) => {
                        setSelectedPatientId(e.target.value);
                        if (e.target.value) {
                          fetchVisitsForPatient(e.target.value);
                        } else {
                          setVisits([]);
                        }
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select a patient</option>
                      {patients.map((patient) => (
                        <option key={patient.id} value={patient.id}>
                          {patient.user.firstName} {patient.user.lastName} - {patient.user.email}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Visit Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Visit (Optional)
                    </label>
                    <select
                      name="visitId"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select a visit</option>
                      {visits.map((visit) => (
                        <option key={visit.id} value={visit.id}>
                          {visit.visitType} - {formatDate(visit.visitDate)} - {visit.diagnosis}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      required
                      placeholder="e.g., Diabetes Type 2"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Diagnosis */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Diagnosis
                    </label>
                    <input
                      type="text"
                      name="diagnosis"
                      placeholder="e.g., Type 2 Diabetes Mellitus"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description *
                    </label>
                    <textarea
                      name="description"
                      required
                      rows={3}
                      placeholder="Describe the treatment plan..."
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Goals */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Goals *
                    </label>
                    <textarea
                      name="goals"
                      required
                      rows={3}
                      placeholder="List the care plan goals..."
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Interventions */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Interventions *
                    </label>
                    <textarea
                      name="interventions"
                      required
                      rows={3}
                      placeholder="Describe the interventions..."
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      name="startDate"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      name="endDate"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                  >
                    Create Care Plan
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Care Plan Modal */}
      {showEditModal && selectedCarePlan && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">View/Edit Care Plan</h3>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedCarePlan(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-6">
                {!isEditMode ? (
                  <>
                    {/* Patient Information */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Patient Information</h4>
                      <p className="text-sm text-gray-900">
                        {selectedCarePlan.patient.user.firstName} {selectedCarePlan.patient.user.lastName}
                      </p>
                      <p className="text-sm text-gray-500">{selectedCarePlan.patient.user.email}</p>
                    </div>

                    {/* Care Plan Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                        <p className="text-sm text-gray-900">{selectedCarePlan.title}</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedCarePlan.status)}`}>
                          {getStatusIcon(selectedCarePlan.status)}
                          <span className="ml-1">{selectedCarePlan.status}</span>
                        </span>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                        <p className="text-sm text-gray-900">{formatDate(selectedCarePlan.startDate)}</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                        <p className="text-sm text-gray-900">
                          {selectedCarePlan.endDate ? formatDate(selectedCarePlan.endDate) : 'Ongoing'}
                        </p>
                      </div>

                      {selectedCarePlan.diagnosis && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Diagnosis</label>
                          <p className="text-sm text-gray-900">{selectedCarePlan.diagnosis}</p>
                        </div>
                      )}

                      {selectedCarePlan.description && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                          <p className="text-sm text-gray-900">{selectedCarePlan.description}</p>
                        </div>
                      )}
                    </div>

                    {/* Goals */}
                    {selectedCarePlan.goals && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Goals</label>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <div className="text-sm text-gray-900">
                            {Array.isArray(selectedCarePlan.goals) 
                              ? selectedCarePlan.goals.map((goal: string, index: number) => (
                                  <div key={index} className="mb-1">• {goal}</div>
                                ))
                              : typeof selectedCarePlan.goals === 'string'
                              ? selectedCarePlan.goals
                              : JSON.stringify(selectedCarePlan.goals, null, 2)
                            }
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Interventions */}
                    {selectedCarePlan.interventions && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Interventions</label>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <div className="text-sm text-gray-900">
                            {Array.isArray(selectedCarePlan.interventions) 
                              ? selectedCarePlan.interventions.map((intervention: string, index: number) => (
                                  <div key={index} className="mb-1">• {intervention}</div>
                                ))
                              : typeof selectedCarePlan.interventions === 'string'
                              ? selectedCarePlan.interventions
                              : JSON.stringify(selectedCarePlan.interventions, null, 2)
                            }
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Visit Information */}
                    {selectedCarePlan.visit && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Associated Visit</h4>
                        <p className="text-sm text-gray-900">
                          Doctor: {selectedCarePlan.visit.doctor.user.firstName} {selectedCarePlan.visit.doctor.user.lastName}
                        </p>
                      </div>
                    )}

                    {/* Created/Updated Info */}
                    <div className="text-xs text-gray-500 border-t pt-4">
                      <p>Created: {formatDate(selectedCarePlan.createdAt)}</p>
                      <p>Last Updated: {formatDate(selectedCarePlan.updatedAt)}</p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowEditModal(false);
                          setSelectedCarePlan(null);
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                      >
                        Close
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsEditMode(true);
                        }}
                        className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                      >
                        Edit Care Plan
                      </button>
                    </div>
                  </>
                ) : (
                  /* Edit Form */
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.currentTarget);
                    
                    const updateData = {
                      title: formData.get('title') as string,
                      diagnosis: formData.get('diagnosis') as string || undefined,
                      description: formData.get('description') as string || undefined,
                      goals: formData.get('goals') as string,
                      interventions: formData.get('interventions') as string,
                      startDate: formData.get('startDate') as string,
                      endDate: formData.get('endDate') as string || undefined,
                      status: formData.get('status') as 'ACTIVE' | 'COMPLETED' | 'ON_HOLD'
                    };

                    try {
                      await api.put(`/care-plans/${selectedCarePlan.id}`, updateData);
                      toast.success('Care plan updated successfully');
                      setIsEditMode(false);
                      setShowEditModal(false);
                      setSelectedCarePlan(null);
                      fetchCarePlans();
                    } catch (error) {
                      console.error('Error updating care plan:', error);
                      toast.error('Failed to update care plan');
                    }
                  }}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Title */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Title *
                        </label>
                        <input
                          type="text"
                          name="title"
                          required
                          defaultValue={selectedCarePlan.title}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      {/* Status */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Status *
                        </label>
                        <select
                          name="status"
                          required
                          defaultValue={selectedCarePlan.status}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="ACTIVE">Active</option>
                          <option value="COMPLETED">Completed</option>
                          <option value="ON_HOLD">On Hold</option>
                        </select>
                      </div>

                      {/* Diagnosis */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Diagnosis
                        </label>
                        <input
                          type="text"
                          name="diagnosis"
                          defaultValue={selectedCarePlan.diagnosis || ''}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      {/* Description */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description
                        </label>
                        <textarea
                          name="description"
                          rows={3}
                          defaultValue={selectedCarePlan.description || ''}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      {/* Start Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Start Date *
                        </label>
                        <input
                          type="date"
                          name="startDate"
                          required
                          defaultValue={selectedCarePlan.startDate.split('T')[0]}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      {/* End Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          End Date
                        </label>
                        <input
                          type="date"
                          name="endDate"
                          defaultValue={selectedCarePlan.endDate ? selectedCarePlan.endDate.split('T')[0] : ''}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>

                    {/* Goals */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Goals *
                      </label>
                      <textarea
                        name="goals"
                        required
                        rows={3}
                        defaultValue={Array.isArray(selectedCarePlan.goals) 
                          ? selectedCarePlan.goals.join('\n')
                          : typeof selectedCarePlan.goals === 'string'
                          ? selectedCarePlan.goals
                          : ''
                        }
                        placeholder="Enter goals (one per line)"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    {/* Interventions */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Interventions *
                      </label>
                      <textarea
                        name="interventions"
                        required
                        rows={3}
                        defaultValue={Array.isArray(selectedCarePlan.interventions) 
                          ? selectedCarePlan.interventions.join('\n')
                          : typeof selectedCarePlan.interventions === 'string'
                          ? selectedCarePlan.interventions
                          : ''
                        }
                        placeholder="Enter interventions (one per line)"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    {/* Form Actions */}
                    <div className="flex justify-end space-x-3 mt-6">
                      <button
                        type="button"
                        onClick={() => {
                          setIsEditMode(false);
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                      >
                        Update Care Plan
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarePlansPage; 